import moment from 'moment';
import { DEFAULT_DATERANGEPICKER_FORMAT } from '../constants/datePickerConstants';
import {
  CurrencyEnum,
  CurrencySymbolEnum,
  FileMimeTypeEnum,
  PaymentProcessEnum,
} from '../enums';
import { Payment } from '../models/crm/types';
import {
  Balance, Execution, Order, Position, RawBalanceData,
  RawExecutionData,
  RawOrderData, RawPositionData,
} from '../models/trading/types';
import { ONE, THOUSAND } from '../store/market-data/charting/constants';
import { UNREALIZED_PROFIT_THOUSAND_HUNDRED_PERCENT } from '../store/reporting/charting/constants';
import { PositionType } from '../store/reporting/types';

import { defaultLogoBase64 } from './assets/logos';
import { restResponseWrapper } from './CommonHelpers';
import { getDateTime } from './temporalDeadZoneFunctions';
import { isBoolean, isNumber as isNumberLodash, isObject } from './typeGuards';
import {
  FileSizeRanges,
  INVALID_VALUE,
  NullableNumber,
  NullableNumberOrString,
  NullableString,
  ParsedPriceAndMantissa,
} from './types';

export const RegExStrings = {
  endingWithDot: /[.]$/g,
  allCommas: /,/g,
  anyComma: /,/,
  everyThirdDigit: /\B(?=(\d{3})+(?!\d))/g,
  startingWithComma: /^,/g,
  whitespaces: /\s/g,
  decimalNumber: /^(\d+(\.\d{0,2})?|\.\d{1,2})$/g,
  languageCode: /[\\?&]lang=([^&#]*)/g,
  plusChar: /\+/g,
  iPhone: /iPhone/i,
  iPad: /iPad/i,
  digitsOnly: /\D/g,
  forwardSlash: /\//g,
};

export function isValidCurrencyValue(value: NullableNumber | string) {
  let newValue = value;

  if (typeof newValue === 'string') {
    if (isNaN(newValue as any as number)) return false;

    newValue = parseFloat(newValue);
  }
  return (newValue != null && !isNaN(newValue!));
}

const getMantissaAndParsedValue = (
  value: NullableString|NullableNumber,
  useMantissa: boolean,
  doRound: boolean,
): ParsedPriceAndMantissa => {
  const defaultMantissa: number = 2;
  let parsedValue: NullableNumberOrString = value;

  if (typeof parsedValue === 'string') parsedValue = parseFloat(parsedValue);

  return useMantissa
    ? { mantissa: doRound ? 0 : defaultMantissa, parsedValue }
    : { parsedValue, mantissa: defaultMantissa };
};

const COMPACT_POWERS: { divisor: number; suffix: string }[] = [
  { divisor: 1e12, suffix: 't' },
  { divisor: 1e9, suffix: 'b' },
  { divisor: 1e6, suffix: 'm' },
  { divisor: 1e3, suffix: 'k' },
];
// `average` abbreviates at half the divisor (round-to-nearest order of magnitude).
const COMPACT_ROUND_FACTOR = 0.5;

const DEFAULT_MANTISSA = 2;

type FormatNumericValueOptions = {
  mantissa?: number;
  average?: boolean;
  thousandSeparated?: boolean;
  spaceSeparated?: boolean;
  currencySymbol?: string;
  currencyPosition?: 'prefix' | 'postfix';
  compactThreshold?: 'floor' | 'round';
};

/**
 * Shared numeric formatter replicating the numbro behaviour previously used here:
 * mantissa rounding (via `fixToFloat`), optional compact suffix (k/m/b/t), thousands
 * separators, and optional currency symbol. Backs `formatCurrency`,
 * `formatCurrencyBySymbol` and `formatNumber`.
 */
function formatNumericValue(value: number, options: FormatNumericValueOptions): string {
  const {
    mantissa = DEFAULT_MANTISSA,
    average = false,
    thousandSeparated = true,
    spaceSeparated = false,
    currencySymbol = '',
    currencyPosition = 'prefix',
    compactThreshold = 'floor',
  } = options;

  let abs = Math.abs(value);
  let suffix = '';

  if (average && abs) {
    const factor = compactThreshold === 'round' ? COMPACT_ROUND_FACTOR : 1;
    const power = COMPACT_POWERS.find(({ divisor }) => abs >= divisor * factor);
    if (power) {
      abs /= power.divisor;
      suffix = power.suffix;
    }
  }

  const fixed = fixToFloat(abs, mantissa).toFixed(mantissa);
  const [integerPart, decimalPart] = fixed.split('.');
  const groupedInteger = thousandSeparated
    ? integerPart.replace(RegExStrings.everyThirdDigit, ',')
    : integerPart;

  let body = decimalPart ? `${groupedInteger}.${decimalPart}` : groupedInteger;

  if (suffix) {
    body = !currencySymbol && spaceSeparated ? `${body} ${suffix}` : `${body}${suffix}`;
  }

  if (currencySymbol) {
    const symbolSeparator = spaceSeparated ? ' ' : '';
    body = currencyPosition === 'postfix'
      ? `${body}${symbolSeparator}${currencySymbol}`
      : `${currencySymbol}${symbolSeparator}${body}`;
  }

  const isNegative = value < 0 && parseFloat(fixed) !== 0;
  return isNegative ? `-${body}` : body;
}

export function formatCurrency(
  value: NullableNumberOrString,
  doRound = false,
  useSuffix = false,
  useMantissa = true,
): string {
  if (!isValidCurrencyValue(value)) return INVALID_VALUE;

  const { mantissa, parsedValue } = getMantissaAndParsedValue(value, useMantissa, doRound);
  return formatNumericValue(parsedValue as number, {
    mantissa,
    average: useSuffix,
    currencySymbol: CurrencySymbolEnum.USD,
    currencyPosition: 'prefix',
    thousandSeparated: true,
    spaceSeparated: false,
  });
}

/**
 * `options` also accepts an object so this matches the signature shared
 * components are written against. `isEuropeanSymbol` and `countryDesignation`
 * are accepted but never acted on: this app trades a single market, and the
 * European price layout they select elsewhere does not apply here.
 */
export function formatCurrencyBySymbol(
  value: NullableNumberOrString,
  currencySymbol: string,
  options: boolean | {
    useSuffix?: boolean;
    isEuropeanSymbol?: boolean;
    countryDesignation?: NullableString;
  } = false,
): string {
  if (!isValidCurrencyValue(value)) return INVALID_VALUE;

  const useSuffix = typeof options === 'boolean' ? options : options.useSuffix ?? false;

  const { mantissa, parsedValue } = getMantissaAndParsedValue(value, true, false);
  return formatNumericValue(parsedValue as number, {
    mantissa,
    average: useSuffix,
    currencySymbol,
    currencyPosition: 'prefix',
    thousandSeparated: true,
    spaceSeparated: false,
  });
}

export function formatNumber(
  value: NullableNumber | string,
  doRound: boolean = false,
  useSuffix: boolean = false,
  spaceSeparated: boolean = true,
  fixedPosition: number = 2,
  thousandSeparated = true,
): string {
  let newValue = value;

  if (!isValidCurrencyValue(newValue)) {
    return INVALID_VALUE;
  }

  if (typeof newValue === 'string') {
    newValue = parseFloat(newValue);
  }

  return formatNumericValue(newValue as number, {
    average: isBoolean(useSuffix) ? useSuffix : false,
    mantissa: doRound ? 0 : fixedPosition,
    thousandSeparated,
    spaceSeparated,
    compactThreshold: 'round',
  });
}

export function formatNumberWithSuffix(
  value: NullableNumber | string,
  suffix: string = '',
  fallBackValue = INVALID_VALUE,
  prefix: string = '',
): string {
  let newValue = formatNumber(value);
  if (newValue === INVALID_VALUE) {
    return fallBackValue;
  }

  return prefix + newValue + suffix;
}

export const getCurrencySymbolByCurrencyCode = (currencyCode: NullableString): string => {
  if (!currencyCode) {
    return '';
  }

  switch (currencyCode.toUpperCase()) {
    case CurrencyEnum.EUR:
      return CurrencySymbolEnum.EURO;
    case CurrencyEnum.USD:
      return CurrencySymbolEnum.USD;
    default:
      return '';
  }
};

export function formatTransaction(payment: Payment, type: 'amount' | 'date') {
  const { amount, currency_code, payment_process, create_date } = payment;

  switch (type) {
    case 'amount':
      return (
        amount < 0 || payment_process === PaymentProcessEnum.Withdrawal
          ? `-${formatCurrencyBySymbol(Math.abs(amount), getCurrencySymbolByCurrencyCode(currency_code))}`
          : `+${formatCurrencyBySymbol(amount, getCurrencySymbolByCurrencyCode(currency_code))}`
      );

    case 'date':
      return getDateTime(true, [ 'format', 'local' ], [ [ `${DEFAULT_DATERANGEPICKER_FORMAT} \u2022 HH:mm` ], [] ], create_date);

    default:
      break;
  }

  return '';
}

export function calculateCost(order: Order, filledSoFar?: number): number | null {
  const { price, orderQty } = order;
  if (!isValidCurrencyValue(price) || !isValidCurrencyValue(orderQty)) {
    return null;
  }

  return (price! * (filledSoFar != null ? filledSoFar : orderQty!));
}

export function parseBalance(rawBalance: RawBalanceData): Balance {
  const {
    account,
    availBp,
    totalBp,
    usedBp,
    equity,
    equityPlusRpnl,
    ordExp,
    posExp,
    rpnl,
    accountCurrency,
  } = rawBalance;

  return {
    account,
    availBp: parseFloat(availBp),
    totalBp: parseFloat(totalBp),
    usedBp: parseFloat(usedBp),
    equity: parseFloat(equity),
    equityPlusRpnl: parseFloat(equityPlusRpnl),
    ordExp: parseFloat(ordExp),
    posExp: parseFloat(posExp),
    rpnl: parseFloat(rpnl),
    accountCurrency,
  };
}

export function parsePosition(rawPosition: RawPositionData): Position {
  const { avgPrice, qty, symbol } = rawPosition;
  return {
    symbol,
    qty: parseInt(qty),
    avgPrice: parseFloat(avgPrice),
  };
}

export function parseNumberOrUndefined(value: NullableString): number | undefined {
  if (value == null) return undefined;

  const parsedValue = Number(value);
  return isNaN(parsedValue) ? undefined : parsedValue;
}

export function parseExecution(rawExecution: RawExecutionData): Execution {
  const {
    execId, timeStamp, fillQty, fillPrice,
  } = rawExecution;
  return {
    execId,
    timeStamp,
    fillQty: parseInt(fillQty),
    fillPrice: parseFloat(fillPrice),
  };
}

export function parseOrder(rawOrder: RawOrderData): Order {
  const {
    orderQty,
    price,
    stopPrice,
    filledSoFar,
    fills,
    avgPriceSoFar,
  } = rawOrder;

  return {
    ...rawOrder,
    orderQty: parseNumberOrUndefined(orderQty!),
    price: parseNumberOrUndefined(price!),
    stopPrice: parseNumberOrUndefined(stopPrice!),
    filledSoFar,
    avgPriceSoFar: parseNumberOrUndefined(avgPriceSoFar),
    fills: fills?.map(item => parseExecution(item)) ?? undefined,

  };
}

/**
 *
 * @param path String with $1, $2, etc. for parameters
 * @param pathParams Array of strings for replacing $N with pathParams[N-1] etc
 */
export function applyPathParams(
  path: string,
  pathParams?: (string | number | boolean | null)[] | null,
) {
  if ((!pathParams || pathParams.length === 0) && path.indexOf('$') === -1) {
    return path;
  }

  let resultPath = path;
  let n = 1;

  while (resultPath.indexOf('$') > -1) {
    if (pathParams) {
      const current = pathParams[n - 1];
      if (isObject(current) || current == null || current === '') {
        throw new Error(`[DataHelpers::applyPathParams] Path parameter ${n} is not valid - '${current}'`);
      }

      resultPath = resultPath.replace(`$${n}`, `${current}`);
    } else {
      throw new Error(`[DataHelpers::applyPathParams] Expected path parameter ${n} is not present`);
    }

    n++;
  }

  return resultPath;
}

/**
 * Returns:
 * - a string of concatenated string values,joined by separator (defaults to `/`)
 * - or if `arrayOfNumeric` is `true` - an array of the numeric keys in the enum `enumValue`
 * @param enumValue The enum to join values of
 * @param separator The string to use as separator
 * @param arrayOfNumeric If true - returns an array of only numeric values
 * @returns A string or an array of strings
 */
export function joinEnum(enumValue: any, separator = '/', arrayOfNumeric = false) {
  const keys = Object.keys(enumValue);
  const values = Object.values(enumValue);

  if (arrayOfNumeric && !isNumber(keys[0]) && !isNumber(values[0])) {
    throw new Error('[DataHelpers/joinEnum] Enum has no numeric values');
  }

  if (arrayOfNumeric) {
    return (
      keys
        .filter(item => isNumber(item))
        .map(item => parseInt(item))
    );
  }

  return (
    values
      .filter(item => !isNumber(item))
      .join(separator)
  );
}

export function extractDataNameFromGetCall(callName: string): string {
  return callName[3].toLowerCase() + callName.substring(4);
}

export function newPromiseWithReduxCacheData(data: any) {
   
  return new Promise(success => success(restResponseWrapper({ data, isFromCache: true })));
}

/**
 * Returns `true` if value is a number
 * Returns `false` otherwise, including if NaN or Infinity by default
 * @param checkInvalidNumberValues If set to false - returns `true` in the cases of NaN and Infinity
 */
export function isNumber(value: any, checkInvalidNumberValues = true) {
  if (value == null) return false;
  if (Array.isArray(value)) return false;
  if (isBoolean(value)) return false;

  const asNumber = Number(value);
  if (checkInvalidNumberValues) return Number.isFinite(asNumber);
  return isNumberLodash(asNumber);
}

/**
 * Returns a string base 64 url string
 * @param imageData The string with the image data
 * @param mimeType Of type FileMimeTypeEnum or string
 * @return Returns a string base 64 url string
 */
export function createBase64DataUrl(
  imageData: NullableString,
  mimeType: FileMimeTypeEnum | string = FileMimeTypeEnum.Jpeg,
) {
  if (!imageData || !mimeType) return `data:${mimeType};base64,${defaultLogoBase64}`;
  if ((imageData).startsWith('data:image')) return imageData;
  return `data:${mimeType};base64,${imageData}`;
}

export function getDataFromBase64DataUrl(base64DataUrl: string, mimeType: string): string {
  return base64DataUrl.replace(`data:${mimeType};base64,`, '');
}

export function fixToFloat(value: number, toFixed: number = 2): number {
  const nullableValue = parseInt('0', 10).toFixed(toFixed);

  if (!value) {
    return parseFloat(nullableValue);
  }

  const isNegativeValue = value < 0;

  // If the exp is undefined or zero...
  if (+toFixed === 0) {
    return Math.round(value);
  }

  let newValue = Math.abs(value);

  // If the value is not a number or the exp is not an integer...
  if (!((toFixed % 1 === 0))) {
    return parseFloat(nullableValue);
  }

  let toStringArrayValue = newValue.toString().split('e');

  newValue = Math.round(+(`${toStringArrayValue[0]}e${toStringArrayValue[1] ? (+toStringArrayValue[1] + toFixed) : toFixed}`));

  toStringArrayValue = newValue.toString().split('e');

  newValue = +(`${toStringArrayValue[0]}e${toStringArrayValue[1] ? (+toStringArrayValue[1] - toFixed) : -toFixed}`);

  return !isNegativeValue ? newValue : -newValue;
}

/**
 * Returns string as Lorem ipsum...
 */
export function getLimitedText(text: NullableString, maxCharacters: number = 100): string {
  if (!text) return INVALID_VALUE;

  const trimmedText = text.trim();

  if (trimmedText.length <= maxCharacters) {
    return trimmedText;
  }

  return `${trimmedText.substr(0, maxCharacters)}...`;
}

export function isOneOf<T extends any = any, V extends any = any>(value: V, values: T[]) {
  return values?.includes(value as any);
}

export const generatingNumberFromBytes = (bytes: number, sizes: FileSizeRanges = 'MB'): number => {
  let step: number;
  switch (sizes) {
    case 'KB': step = 10; break;
    case 'GB': step = 30; break;
    default: step = 20; break;
  }

  return bytes / (2 ** step);
};

 
export const unrealizedPnL = (quantity: NullableNumber, marketPrice: NullableNumber, averageOpeningPrice: number) => ((quantity == null || marketPrice == null) ? null : (quantity * (marketPrice - averageOpeningPrice)));

export const roundUnrealizedProfit = (unrealizedProfit: NullableNumber, hasDecimal: boolean): string => {
  if (unrealizedPnL == null) return INVALID_VALUE;

  let unrealizedProfitValue: string = '';

  if (hasDecimal) {
    unrealizedProfitValue = formatCurrency(unrealizedProfit);
  } else {
    unrealizedProfitValue = formatCurrency(unrealizedProfit, true);
  }

  return unrealizedProfit! > 0 ? `+${unrealizedProfitValue}` : unrealizedProfitValue;
};

 
export const calcUnrealizedProfitPercent = (positionType: string, marketPrice: NullableNumber, averageOpeningPrice: number): string | number => {
  if (marketPrice == null) return INVALID_VALUE;

  const value = positionType === PositionType.Buy
    ? ((marketPrice - averageOpeningPrice) / averageOpeningPrice) * 100
    : ((averageOpeningPrice - marketPrice) / averageOpeningPrice) * 100;

  return Math.abs(value) >= UNREALIZED_PROFIT_THOUSAND_HUNDRED_PERCENT ? Math.floor(value) : formatNumber(value);
};

export const getStringLiteral = <T>(elements: T | T[], separator: string = ', '): string => {
  if (Array.isArray(elements)) {
    return elements.join(separator);
  }
  return String(elements);
};

export const roundPercentValues = (value: number) => {
  switch (true) {
    case value > THOUSAND:
      return formatNumber(value, true);
    case value < ONE:
      return formatNumber(value);
    default: return formatNumber(value, false, false, true, 1);
  }
};

export const roundNumberToTwoDecimals = (value: number) => (Math.round((value + Number.EPSILON) * 100) / 100);

type CompactMetricFormatOptions = {
  withCurrency?: boolean;
  currencySymbol?: string;
  useSuffix?: boolean;
  uppercaseSuffix?: boolean;
};

const suffixWithTrailingZeroesPattern = /(\.\d*?[1-9])0+([kmb])$/i;
const suffixWithOnlyZeroesPattern = /\.0+([kmb])$/i;
const whitespaceBeforeSuffixPattern = /\s+([kmb])$/i;
const suffixPattern = /([kmb])$/i;

const normalizeCompactSuffix = (value: string, uppercaseSuffix: boolean) => {
  const normalized = value
    .replace(suffixWithTrailingZeroesPattern, '$1$2')
    .replace(suffixWithOnlyZeroesPattern, '$1')
    .replace(whitespaceBeforeSuffixPattern, '$1');

  if (!uppercaseSuffix) {
    return normalized;
  }

  const suffixMatch = normalized.match(suffixPattern);

  if (!suffixMatch) {
    return normalized;
  }

  return normalized.replace(suffixMatch[0], suffixMatch[1].toUpperCase());
};

export function formatCompactMetricValue(
  value: NullableNumberOrString,
  options: CompactMetricFormatOptions = {},
): string {
  const {
    withCurrency = false,
    currencySymbol = CurrencySymbolEnum.USD,
    useSuffix = true,
    uppercaseSuffix = true,
  } = options;

  const compactCurrencyValue = formatCurrencyBySymbol(value, currencySymbol, { useSuffix });
  const formatted = withCurrency
    ? compactCurrencyValue
    : compactCurrencyValue.replace(currencySymbol, '');

  return normalizeCompactSuffix(formatted, uppercaseSuffix);
}

export const pluralizeByCount = (count: number, singular: string, plural: string): string => (
  Math.abs(count) === ONE ? singular : plural
);

/**
 * `symbol` is accepted so this matches the signature shared components are written
 * against. It is never read: this app trades a single market, so a row without a
 * currency code is priced in dollars rather than resolved from the symbol's country.
 */
export const getCurrencySymbolByCurrencyCodeOrSymbol = (
  currencyCode: NullableString,
  symbol: string,
): string => getCurrencySymbolByCurrencyCode(currencyCode) || CurrencySymbolEnum.USD;

export function getTimeDiffFromNow(dateFromTimestamp: number, withoutSuffix: boolean = false): string {
  return moment(dateFromTimestamp).fromNow(withoutSuffix);
}
